<template>
  <v-container class="px-0">
    <v-row v-if="props.primaryDiagnosisIcd10Code && props.primaryDiagnosisName">
      <v-col cols="3" class="pb-0">
        <FeedbackInputWrapper
          :field-id="'primaryDiagnosisIcd10Code'"
          :order-key="props.orderKey"
          :model-value="primaryDiagnosis.icd10Code"
          label="primaryDiagnosisIcd10Code">
          <template #default="{ onInput, onChange, onFocus, onBlur }">
            <v-text-field
              v-model="primaryDiagnosis.icd10Code"
              label="ICD 10 Code(s)"
              :readonly="!props.isEditable"
              :disabled="!props.isEditable"
              class="lh--force-white-background"
              placeholder=""
              @input="onInput"
              @change="
                (e) => {
                  onChange(e);
                  emit('updatePrimaryDiagnosis', {
                    icd10Code: primaryDiagnosis.icd10Code,
                    name: primaryDiagnosis.name,
                  });
                }
              "
              @focus="onFocus"
              @blur="onBlur" />
          </template>
        </FeedbackInputWrapper>
      </v-col>
      <v-col cols="8" class="pb-0">
        <FeedbackInputWrapper
          :field-id="'primaryDiagnosisName'"
          :order-key="props.orderKey"
          :model-value="primaryDiagnosis.name"
          label="primaryDiagnosisName">
          <template #default="{ onInput, onChange, onFocus, onBlur }">
            <v-text-field
              v-model="primaryDiagnosis.name"
              label="Primary Diagnosis"
              :readonly="!props.isEditable"
              :disabled="!props.isEditable"
              class="lh--force-white-background"
              placeholder=""
              @input="onInput"
              @change="
                (e) => {
                  onChange(e);
                  emit('updatePrimaryDiagnosis', {
                    icd10Code: primaryDiagnosis.icd10Code,
                    name: primaryDiagnosis.name,
                  });
                }
              "
              @focus="onFocus"
              @blur="onBlur" />
          </template>
        </FeedbackInputWrapper>
      </v-col>
      <v-col v-if="props.isEditable" cols="1" class="pb-0">
        <v-btn v-if="props.isEditable" icon="mdi-delete" variant="text" color="error" @click="unSetPrimaryDiagnosis()">
        </v-btn>
      </v-col>
      <v-col v-if="llmPrediction" cols="12" class="pt-0">
        <span v-if="llmPrediction.isHumanReviewRequested" class="text-red-accent-2"> Review Needed </span>
        <span v-if="llmPrediction.context"> Context: {{ llmPrediction.context }} </span>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col v-if="props.isEditable" cols="12">
        <DiagnosticICD10CodeAutocomplete
          :data="{ label: 'Primary Diagnosis', searchId: generateRandom4DigitNumber() }"
          :required="true"
          @icd10-selected="selectedPrimaryDiagnosis = $event" />
      </v-col>
      <v-col v-if="!isPrimaryDiagnosisValid && props.formButtonClicked" cols="12">
        <v-alert type="error" variant="tonal" density="compact" class="mb-3"> Please enter Primary Diagnosis. </v-alert>
      </v-col>
    </v-row>
    <v-row v-if="props.isEditable && secondaryDiagnosisIndex > 0">
      <v-col cols="12" class="pt-4">
        <div class="text-body-3-1 weight-600">SECONDARY DIAGNOSES</div>
        <v-divider class="my-2"></v-divider>
      </v-col>
      <v-col v-for="i in secondaryDiagnosisIndex" :key="i" cols="12">
        <v-row v-if="otherDiagnosis[i - 1].icd10Code && otherDiagnosis[i - 1].name">
          <v-col cols="3">
            <FeedbackInputWrapper
              :field-id="'secondaryDiagnosisIcd10Code' + (i - 1)"
              :order-key="props.orderKey"
              :model-value="otherDiagnosis[i - 1].icd10Code"
              :label="'secondaryDiagnosisIcd10Code' + (i - 1)">
              <template #default="{ onInput, onChange, onFocus, onBlur }">
                <v-text-field
                  v-model="otherDiagnosis[i - 1].icd10Code"
                  label="ICD 10 Code(s)"
                  :readonly="!props.isEditable"
                  :disabled="!props.isEditable"
                  class="lh--force-white-background"
                  placeholder=""
                  @input="onInput"
                  @change="
                    (e) => {
                      onChange(e);
                      emit('updateOtherDiagnosis', i - 1, {
                        icd10Code: otherDiagnosis[i - 1].icd10Code,
                        name: otherDiagnosis[i - 1].name,
                      });
                    }
                  "
                  @focus="onFocus"
                  @blur="onBlur" />
              </template>
            </FeedbackInputWrapper>
          </v-col>
          <v-col cols="8">
            <FeedbackInputWrapper
              :field-id="'secondaryDiagnosisName' + (i - 1)"
              :order-key="props.orderKey"
              :model-value="otherDiagnosis[i - 1].name"
              :label="'secondaryDiagnosisName' + (i - 1)">
              <template #default="{ onInput, onChange, onFocus, onBlur }">
                <v-text-field
                  v-model="otherDiagnosis[i - 1].name"
                  label="Diagnosis"
                  :readonly="!props.isEditable"
                  :disabled="!props.isEditable"
                  class="lh--force-white-background"
                  placeholder=""
                  @input="onInput"
                  @change="
                    (e) => {
                      onChange(e);
                      emit('updateOtherDiagnosis', i - 1, {
                        icd10Code: otherDiagnosis[i - 1].icd10Code,
                        name: otherDiagnosis[i - 1].name,
                      });
                    }
                  "
                  @focus="onFocus"
                  @blur="onBlur" />
              </template>
            </FeedbackInputWrapper>
          </v-col>
          <v-col cols="1" class="d-flex align-center">
            <v-btn
              v-if="props.isEditable"
              icon="mdi-delete"
              variant="text"
              color="error"
              @click="unSetSecondaryDiagnosis(i - 1)">
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col v-if="props.isEditable" cols="12">
            <div v-if="secondaryDiagnosisIndex < secondaryDiagnosisMaxIndex">
              <DiagnosticICD10CodeAutocomplete
                :data="{ label: '', searchId: generateRandom4DigitNumber() }"
                @icd10-selected="selectedSecondaryDiagnosis = $event" />
            </div>
            <div v-else>
              <v-btn color="primary" prepend-icon="mdi-plus" variant="outlined" @click="emit('addOtherDiagnosis')">
                Add Secondary Diagnosis
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, watch, computed, onBeforeMount } from "vue";
import { generateRandom4DigitNumber } from "@/utils";
import DiagnosticICD10CodeAutocomplete from "@/components/DiagnosticICD10CodeAutocomplete.vue";
import FeedbackInputWrapper from "@/components/FeedbackInputWrapper.vue";

const props = defineProps({
  orderKey: {
    type: String,
    default() {
      return "";
    },
  },
  primaryDiagnosisName: {
    type: String,
    default() {
      return null;
    },
  },
  primaryDiagnosisIcd10Code: {
    type: String,
    default() {
      return null;
    },
  },
  secondaryDiagnosis: {
    type: Array,
    default() {
      return [
        {
          icd10Code: null,
          name: null,
        },
      ];
    },
  },
  medication: {
    type: String,
    default() {
      return "";
    },
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
  llmPrediction: {
    type: Object,
    default() {
      return {
        isHumanReviewRequested: false,
        context: null,
      };
    },
  },
});

const emit = defineEmits(["updatePrimaryDiagnosis", "updateOtherDiagnosis", "addOtherDiagnosis"]);

const primaryDiagnosis = ref({
  name: props.primaryDiagnosisName,
  icd10Code: props.primaryDiagnosisIcd10Code,
});
const otherDiagnosis = ref(props.secondaryDiagnosis ? props.secondaryDiagnosis : [{ icd10Code: null, name: null }]);
const selectedPrimaryDiagnosis = ref(null);
const selectedSecondaryDiagnosis = ref(null);
const secondaryDiagnosisMaxIndex = computed(() => otherDiagnosis.value.length);
const secondaryDiagnosisIndex = ref(1);

onBeforeMount(async () => {
  if (props.isEditable) {
    if (otherDiagnosis.value.findIndex((item) => !item.name) === -1) {
      addSecondaryDiagnosis();
    }
    secondaryDiagnosisIndex.value = otherDiagnosis.value.findIndex((item) => !item.name) + 1;
  } else {
    secondaryDiagnosisIndex.value = otherDiagnosis.value.filter((item) => item.name).length;
  }
});

function addSecondaryDiagnosis() {
  emit("addOtherDiagnosis");
}

watch(selectedPrimaryDiagnosis, (newSelectedPrimaryDiagnosis) => {
  if (newSelectedPrimaryDiagnosis) {
    emit("updatePrimaryDiagnosis", {
      icd10Code: newSelectedPrimaryDiagnosis.icd10Code,
      name: newSelectedPrimaryDiagnosis.name,
    });
  }
});

watch(selectedSecondaryDiagnosis, (newSelectedSecondaryDiagnosis) => {
  if (newSelectedSecondaryDiagnosis) {
    emit("updateOtherDiagnosis", {
      index: secondaryDiagnosisIndex.value,
      icd10Code: newSelectedSecondaryDiagnosis.icd10Code,
      name: newSelectedSecondaryDiagnosis.name,
    });
    if (secondaryDiagnosisIndex.value < secondaryDiagnosisMaxIndex.value) {
      secondaryDiagnosisIndex.value++;
    }
  }
});

const isPrimaryDiagnosisValid = computed(() => {
  if (props.primaryDiagnosisName && props.primaryDiagnosisIcd10Code) {
    return props.primaryDiagnosisIcd10Code && props.primaryDiagnosisName;
  } else {
    return false;
  }
});

function unSetPrimaryDiagnosis() {
  emit("updatePrimaryDiagnosis", {
    icd10Code: null,
    name: null,
  });
  selectedPrimaryDiagnosis.value = null;
}

function unSetSecondaryDiagnosis(index) {
  emit("updateOtherDiagnosis", {
    index,
    icd10Code: null,
    name: null,
  });
  selectedSecondaryDiagnosis.value = null;
  secondaryDiagnosisIndex.value--;
}

watch(
  () => props.primaryDiagnosisName,
  (newValue) => {
    primaryDiagnosis.value.name = newValue;
  }
);

watch(
  () => props.primaryDiagnosisIcd10Code,
  (newValue) => {
    primaryDiagnosis.value.icd10Code = newValue;
  }
);
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";
</style>
